import { SX_API_KEY } from "@/config/env"
import type { InfiniteData } from "@tanstack/react-query"
import { useInfiniteQuery } from "@tanstack/react-query"
import { constants } from "helpers"

// ================ TYPES ================

interface SxBetTrade {
  marketHash?: string
  baseToken?: string
  bettingOutcome?: string
  gameLabel?: string
  gameTime?: string
  settleDate?: string
  leagueLabel?: string
  bettingOutcomeLabel?: string
  betTime?: string
  market?: any
  // Dynamic properties
  [key: string]: any
}

interface SxBetGroupedTrade {
  marketHash: string
  baseToken: string
  bettingOutcome: string
  gameLabel: string
  gameTime: string
  settleDate: string
  leagueLabel: string
  bettingOutcomeLabel: string
  totalStake: number
  netReturn: number
  settleNetReturnValue: string
  weightedAverageOdds: string
  netReturnBetTimeValue: string
  totalBetTimeValue: string
  trades: SxBetTrade[]
  market: any
}

interface SxBetApiResponse {
  status?: string
  data: {
    trades: SxBetTrade[] | SxBetGroupedTrade[]
    count: number
  }
}

interface PageData {
  trades: SxBetTrade[]
  total: number
  page: number
  perPage: number
}

// ================ CONSTANTS ================

const getSxBetApiUrl = constants.getSxBetApiUrl()
const SX_BET_API_URL = `${getSxBetApiUrl}/trades/consolidated/grouped`
const SX_BET_GROUPED_API_URL = `${getSxBetApiUrl}/trades/consolidated/grouped`

const IS_DEV = SX_API_KEY !== ""
const DEFAULT_PER_PAGE = 10

// ================ UTILITY FUNCTIONS ================

/**
 * Groups trades by game and bet time, creating consolidated bets ONLY when
 * multiple bets are for the same game AND have the same bet time.
 * For different bet times in the same game, creates separate individual bets.
 */
function groupTradesByGameAndTime(
  groupedTrades: SxBetGroupedTrade[]
): SxBetTrade[] {
  // First, process each grouped trade and extract individual trades with game context
  const allIndividualTrades: SxBetTrade[] = []

  groupedTrades.forEach(groupedTrade => {
    if (!groupedTrade.trades || groupedTrade.trades.length === 0) {
      return
    }

    // For each individual trade in the grouped trade, add game context but use individual trade values
    groupedTrade.trades.forEach(trade => {
      const enrichedTrade: SxBetTrade = {
        ...trade,
        // Add grouped trade properties for context (game info, market info)
        marketHash: groupedTrade.marketHash,
        gameLabel: groupedTrade.gameLabel,
        gameTime: groupedTrade.gameTime,
        settleDate: groupedTrade.settleDate,
        leagueLabel: groupedTrade.leagueLabel,
        bettingOutcomeLabel: groupedTrade.bettingOutcomeLabel,
        market: groupedTrade.market,

        // Use individual trade's financial values (not the aggregated ones from groupedTrade)
        totalStake: trade.totalStake || trade.totalBetTimeValue,
        netReturn: trade.netReturn,
        settleNetReturnValue: trade.settleNetReturnValue,
        weightedAverageOdds: trade.weightedAverageOdds,
        netReturnBetTimeValue: trade.netReturnBetTimeValue,
        totalBetTimeValue: trade.totalBetTimeValue,

        // Ensure we have the individual trade's bet time
        betTime: trade.betTime,
      }
      allIndividualTrades.push(enrichedTrade)
    })
  })

  // Now group by game AND bet time (same game + same bet time = group them)
  const gameAndTimeGroups = new Map<string, SxBetTrade[]>()

  allIndividualTrades.forEach(trade => {
    // Create a unique key combining game and bet time
    const groupKey = `${trade.gameLabel}_${trade.gameTime}_${trade.betTime}`

    if (!gameAndTimeGroups.has(groupKey)) {
      gameAndTimeGroups.set(groupKey, [])
    }
    gameAndTimeGroups.get(groupKey)!.push(trade)
  })

  // Process groups: if group has multiple trades, create consolidated bet
  // If group has single trade, keep it as individual bet
  const result: SxBetTrade[] = []

  gameAndTimeGroups.forEach(trades => {
    if (trades.length > 1) {
      // Multiple bets for same game at same time - create grouped bet with combined values
      const totalStake = trades.reduce(
        (sum, trade) => sum + Number(trade.totalStake || 0),
        0
      )
      const totalNetReturn = trades.reduce(
        (sum, trade) => sum + Number(trade.netReturn || 0),
        0
      )
      const totalBetTimeValue = trades.reduce(
        (sum, trade) => sum + Number(trade.totalBetTimeValue || 0),
        0
      )

      const consolidatedBet: SxBetTrade = {
        ...trades[0], // Use first trade as base
        // Override with calculated combined values
        totalStake: totalStake.toString(),
        netReturn: totalNetReturn.toString(),
        totalBetTimeValue: totalBetTimeValue.toString(),
        netReturnBetTimeValue: totalNetReturn,
        settleNetReturnValue: totalNetReturn.toString(),
        // Metadata to indicate this is a grouped bet
        isGroupedBet: true,
        allTrades: trades,
      }
      result.push(consolidatedBet)
    } else {
      // Single bet - keep as individual bet with its own values
      const individualBet = {
        ...trades[0],
        isGroupedBet: false,
      }
      result.push(individualBet)
    }
  })

  // Sort by bet time (newest first)
  return result.sort((a, b) => {
    const timeA = new Date(a.betTime || 0).getTime()
    const timeB = new Date(b.betTime || 0).getTime()
    return timeB - timeA
  })
}

/**
 * Sorts trades by bet time (newest first)
 */
function sortTradesByBetTime(trades: SxBetTrade[]): SxBetTrade[] {
  return trades.sort((a, b) => {
    const timeA = new Date(a.betTime || 0).getTime()
    const timeB = new Date(b.betTime || 0).getTime()
    return timeB - timeA
  })
}

/**
 * Creates API headers with optional dev API key
 */
function createApiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  if (IS_DEV && SX_API_KEY) {
    headers["x-api-key"] = SX_API_KEY
  }
  return headers
}

/**
 * Creates URL search params for API calls
 */
function createApiParams(options: {
  account: string
  chainId: number
  page: number
  perPage: number
  settled?: boolean
  tradeStatus?: string
  sortBy?: string
  sortAsc?: string
}): URLSearchParams {
  const params = new URLSearchParams({
    bettor: options.account,
    page: options.page.toString(),
    perPage: options.perPage.toString(),
    sortBy: options.sortBy || "betTime",
    sortAsc: options.sortAsc || "false",
    chainId: options.chainId.toString(),
  })

  if (options.settled !== undefined) {
    params.set("settled", options.settled.toString())
  }

  if (options.tradeStatus) {
    params.set("tradeStatus", options.tradeStatus)
  }

  return params
}

/**
 * Fetches trades from API with error handling
 */
async function fetchTrades(
  url: string,
  params: URLSearchParams,
  headers: Record<string, string>
): Promise<SxBetApiResponse> {
  const response = await fetch(`${url}?${params}`, { headers })

  if (!response.ok) {
    throw new Error(
      `Failed to fetch trades: ${response.status} ${response.statusText}`
    )
  }

  return response.json()
}

/**
 * Fetches both settled and unsettled trades for dev environment
 */
async function fetchDevTrades(
  account: string,
  chainId: number,
  page: number
): Promise<PageData> {
  const headers = createApiHeaders()

  const settledParams = createApiParams({
    account,
    chainId,
    page: page - 1, // Convert to 0-based for grouped API
    perPage: DEFAULT_PER_PAGE,
    settled: true,
    tradeStatus: "SUCCESS,PENDING,FAILED",
    sortBy: "gameTime",
    sortAsc: "false",
  })

  const unsettledParams = createApiParams({
    account,
    chainId,
    page: page - 1, // Convert to 0-based for grouped API
    perPage: DEFAULT_PER_PAGE,
    settled: false,
    tradeStatus: "SUCCESS,PENDING,FAILED",
    sortBy: "gameTime",
    sortAsc: "false",
  })

  const [settledResponse, unsettledResponse] = await Promise.all([
    fetchTrades(SX_BET_GROUPED_API_URL, settledParams, headers),
    fetchTrades(SX_BET_GROUPED_API_URL, unsettledParams, headers),
  ])

  // Process grouped trades
  const settledTrades = groupTradesByGameAndTime(
    settledResponse.data.trades as SxBetGroupedTrade[]
  )
  const unsettledTrades = groupTradesByGameAndTime(
    unsettledResponse.data.trades as SxBetGroupedTrade[]
  )

  // Combine and sort all trades
  const allTrades = sortTradesByBetTime([...settledTrades, ...unsettledTrades])
  const totalCount = settledResponse.data.count + unsettledResponse.data.count

  // Debug logging
  console.log("🔍 SX Dev Trades:", {
    totalTrades: allTrades.length,
    totalCount,
    groupedBetsCount: allTrades.filter(t => t.isGroupedBet).length,
    settledCount: settledTrades.length,
    unsettledCount: unsettledTrades.length,
  })

  return {
    trades: allTrades,
    total: totalCount,
    page: page,
    perPage: DEFAULT_PER_PAGE,
  }
}

/**
 * Fetches both settled and unsettled trades for production environment
 */
async function fetchProductionTrades(
  account: string,
  chainId: number,
  page: number
): Promise<PageData> {
  const headers = createApiHeaders()

  const settledParams = createApiParams({
    account,
    chainId,
    page,
    perPage: DEFAULT_PER_PAGE,
    settled: true,
  })

  const unsettledParams = createApiParams({
    account,
    chainId,
    page,
    perPage: DEFAULT_PER_PAGE,
    settled: false,
  })

  const [settledResponse, unsettledResponse] = await Promise.all([
    fetchTrades(SX_BET_API_URL, settledParams, headers),
    fetchTrades(SX_BET_API_URL, unsettledParams, headers),
  ])

  // Combine and sort trades
  const allTrades = sortTradesByBetTime([
    ...(settledResponse.data.trades as SxBetTrade[]),
    ...(unsettledResponse.data.trades as SxBetTrade[]),
  ])

  const totalCount = settledResponse.data.count + unsettledResponse.data.count

  return {
    trades: allTrades,
    total: totalCount,
    page: page,
    perPage: DEFAULT_PER_PAGE,
  }
}

// ================ HOOKS ================

export function useUserSxBets(account?: string, chainId?: number) {
  return useInfiniteQuery<
    PageData,
    Error,
    InfiniteData<PageData>,
    (string | number | undefined)[],
    number
  >({
    queryKey: ["sxBetBets", account, chainId],
    enabled: !!account && !!chainId,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const page = pageParam as number

      if (!account || !chainId) {
        throw new Error("Account and chainId are required")
      }

      try {
        if (IS_DEV) {
          return await fetchDevTrades(account, chainId, page)
        } else {
          return await fetchProductionTrades(account, chainId, page)
        }
      } catch (error) {
        console.error("Failed to fetch SX trades:", error)
        throw error
      }
    },
    getNextPageParam: lastPage => {
      const { page, perPage, total } = lastPage
      return page * perPage < total ? page + 1 : undefined
    },
  })
}
